import { Lightning, Router } from '@lightningjs/sdk';
import { reportPageView } from './analytics/reportingServicePage';
import { loginWidgets, WidgetNames } from 'config/routes';

const MODAL_PAGE_NAME: Partial<Record<keyof Router.Widgets, string>> = {
  // Login modals
  LoginLanding: 'Initial Login',
  EmailInputModal: 'Email Input',
  SignUpModal: 'Sign Up',
  EmailConfirmationModal: 'Email Confirmation',
  SignInConfirmationModal: 'Login Confirmation',
  CreatePasswordModal: 'Sign Up by Password',
  SignInModal: 'Sign In',
  SignInPasswordModal: 'Sign In by Password',
  ForgotPasswordModal: 'Forgot Password',
  ForgotPasswordConfirmationModal: 'Reset Password Link Confirmation',
  SignOutModal: 'Sign Out',

  ExitModal: 'Exit Confirmation',
  ResetParentalPin: 'Reset Parental Pin',
  SetParentalPin: 'Set Parental Pin',
  EnterParentalPin: 'Enter Parental Pin',
  RemoveConfirmationModal: 'Remove Confirmation',
};

// Modals that do not block in-app notifications from displaying
const NON_BLOCKING_MODALS: WidgetNames[] = [...loginWidgets];

type ModalComponentType = {
  hide?: () => void;
  show?: () => void;
  modalInit?: (data: unknown) => void;
};

type ModalOptions = {
  replaceCurrent?: boolean;
  data?: unknown;
  openModalAboveCurrent?: boolean;
};

type ModalHistory = {
  modal: keyof Router.Widgets;
} & ModalOptions;

export default class ModalService {
  private modalHistoryStack: ModalHistory[] = [];
  private currentHistory: ModalHistory | undefined;

  /**
   * Closes currently open/focused modal. Opens previous modal if available otherwise focus' page
   */
  close() {
    const previous = this.modalHistoryStack.pop();

    if (previous) {
      this.setCurrentModal(previous);
    } else {
      this.closeCurrent();
      Router.focusPage();
    }
  }

  private closeCurrent() {
    this.getCurrentModal()?.hide?.();
    this.currentHistory = undefined;
  }

  /**
   * Closes the current modal and clears the modal history stack.  Assumes all previous modals have already been hidden.
   */
  closeAll() {
    this.closeCurrent();
    this.modalHistoryStack = [];
    Router.focusPage();
  }

  closeUntil(modal: keyof Router.Widgets) {
    if (this.modalHistoryStack.length === 0) return;

    let index = null;
    const previous = this.modalHistoryStack.find((history, i) => {
      if (history.modal === modal) {
        index = i;
        return true;
      }
    });

    this.modalHistoryStack.length = index ?? 0;

    this.closeCurrent();
    if (previous) {
      this.setCurrentModal(previous);
    }
  }

  /**
   * Opens and focus' modal, if a modal is already open calls modalAfter unless `replaceCurrent` is passed
   * @param modal Name of the modal (must be configured for the current route)
   * @param options Options stored/used in modal history item. `data` will be passed to the modal
   */
  open(modal: keyof Router.Widgets, options?: ModalOptions) {
    const newHistory: ModalHistory = {
      modal,
      ...options,
    };

    if (this.currentHistory && !options?.replaceCurrent) {
      const { modal: currentModal, ...currentOptions } = this.currentHistory;
      this.modalAfter(currentModal, currentOptions);
    }

    this.setCurrentModal(newHistory, options?.openModalAboveCurrent);
  }

  /**
   * Adds a modal to the history stack to be opened after the current modal is closed. If no modal is currently open, function does nothing
   * @param modal Name of the modal (must be configured for the current route)
   * @param options Options stored/used in modal history item. `data` will be passed to the modal
   */
  modalAfter(modal: keyof Router.Widgets, options?: ModalOptions) {
    // if there is no current modal we shouldn't add a modal after (this could cause unexpected behaviour)
    if (this.currentHistory === undefined) return;

    this.modalHistoryStack.push({ modal, ...options });
  }

  isInHistory(modal: keyof Router.Widgets): boolean {
    if (this.currentHistory?.modal === modal) return true;

    return !!this.modalHistoryStack.find(history => history.modal === modal);
  }

  private setCurrentModal(
    newHistory: ModalHistory,
    openModalAboveCurrent = false,
  ) {
    const previousModal = this.currentHistory?.modal;

    if (this.currentHistory && !openModalAboveCurrent) {
      this.getCurrentModal()?.hide?.();
    }

    this.currentHistory = newHistory;
    Router.focusWidget(newHistory.modal);
    this.getCurrentModal()?.modalInit?.(newHistory.data);
    this.getCurrentModal()?.show?.();

    // Report pageView when the active modal changes (covers open + back navigation)
    if (previousModal !== newHistory.modal) {
      const pageName = this.getPageName(newHistory.modal);
      if (pageName) reportPageView(pageName);
    }
  }

  private getCurrentModal(): ModalComponentType | undefined {
    if (this.currentHistory === undefined) return undefined;

    return Router.getActiveWidget() as ModalComponentType;
  }

  isOtherModalActive(modal: Lightning.Component) {
    const current = this.getCurrentModal();
    return !!current && current !== modal;
  }

  isBlockingModalActive(): boolean {
    return (
      !!this.currentHistory &&
      !NON_BLOCKING_MODALS.includes(this.currentHistory.modal as WidgetNames)
    );
  }

  private getPageName(modal: keyof Router.Widgets): string | undefined {
    return MODAL_PAGE_NAME[modal];
  }
}
